<template>
	<div class="caller-tile">
		<video ref="video" autoplay playsinline muted></video>
		<div v-if="!hasVideo" class="caller-tile-off"><b-icon icon="camera-video-off-fill" /></div>
		<div class="caller-tile-name text-truncate">{{name}}</div>
		<b-icon v-if="muted" icon="mic-mute-fill" class="caller-tile-muted" title="Mic muted" />
		<!-- Always visible, unlike the hover actions: a Dynamic grid only draws guests on stage, so this is the first thing to check when one is missing. -->
		<b-button
			v-if="onStage !== null"
			size="sm"
			class="caller-tile-stage"
			:variant="onStage ? 'success' : 'secondary'"
			:title="onStage ? 'On stage - click to take off' : 'Not on stage - click to put on stage'"
			@click="$emit('toggle-stage')"
		><b-icon icon="broadcast" /></b-button>
		<div class="caller-tile-hover">
			<b-button size="sm" variant="primary" @click="$emit('add')"><b-icon icon="plus-lg" class="mr-1" />Add to scene</b-button>
			<slot name="actions" />
		</div>
	</div>
</template>

<script>
import { watchPicture } from '@/streamPicture';

// One participant's (or one extra camera's / screen share's) live preview in the studio's guest strip. Either
// shows a local stream it's handed, or - for someone else's - opens its own receiver through the call session.
export default {
	props: {
		name: { type: String, default: '' },
		stream: { default: null },
		callSession: { type: Object, default: null },
		callerId: { type: String, default: null },
		deviceId: { type: String, default: null },
		// null for tiles that aren't a guest (yourself, extra cameras, screens) and so have no stage state.
		onStage: { type: Boolean, default: null },
		muted: { type: Boolean, default: false },
	},
	data() { return { hasVideo: false }; },
	watch: {
		stream() { this.attach(this.stream); },
		callerId() { this.connect(); },
		deviceId() { this.connect(); },
	},
	mounted() {
		if(this.callerId) this.connect();
		else this.attach(this.stream);
	},
	beforeDestroy() {
		this.receiver?.close();
		this.stopWatching?.();
	},
	methods: {
		attach(stream) {
			const video = this.$refs.video;
			if(!video) return;
			video.srcObject = stream || null;
			this.stopWatching?.();
			this.stopWatching = watchPicture(stream, has => { this.hasVideo = has; }, { video: this.deviceId ? null : video });
		},
		connect() {
			this.receiver?.close();
			this.receiver = this.callSession.openReceiver(this.callerId, this.deviceId);
			this.receiver.onStream(stream => this.attach(stream));
		},
	},
};
</script>

<style lang="scss" scoped>
.caller-tile {
	position: relative;
	flex: 0 0 auto;
	width: 160px;
	height: 90px;
	border-radius: .3rem;
	overflow: hidden;
	background: #000;

	video {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
}

.caller-tile-stage {
	position: absolute;
	top: 4px;
	right: 4px;
	z-index: 2;
	padding: .05rem .3rem;
	line-height: 1.3;
	opacity: .9;
}

.caller-tile-muted {
	position: absolute;
	bottom: 4px;
	right: 4px;
	padding: 2px;
	width: 1.3em;
	height: 1.3em;
	border-radius: 50%;
	background: rgba(220, 53, 69, .9);
	color: #fff;
}

.caller-tile-off {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, .08);
	color: rgba(255, 255, 255, .5);
	font-size: 1.6rem;
}

.caller-tile-name {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	padding: .1rem .4rem;
	font-size: .75rem;
	background: linear-gradient(transparent, rgba(0, 0, 0, .75));
}

.caller-tile-hover {
	position: absolute;
	inset: 0;
	display: none;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: .2rem;
	background: rgba(0, 0, 0, .55);

	// Up to three actions have to fit in a 90px-tall tile.
	::v-deep .btn {
		padding: .1rem .4rem;
		font-size: .7rem;
		line-height: 1.3;
	}
}

.caller-tile:hover .caller-tile-hover { display: flex; }
</style>
